import { useState } from "react";
import { Download, RotateCw, Trash2 } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function SignupsTable({ title, testid, rows, columns, fileBase, onDelete, onResend }) {
    const [busyId, setBusyId] = useState(null);
    const [failedId, setFailedId] = useState(null);

    const exportCsv = () => {
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const head = columns.map((c) => esc(c.label)).join(",");
        const body = rows.map((r) => columns.map((c) => esc(c.value(r))).join(",")).join("\r\n");
        const blob = new Blob(["\uFEFF" + head + "\r\n" + body], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileBase}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <section className="rounded-3xl border border-clay bg-white" data-testid={testid}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-clay p-5 sm:p-6">
                <h3 className="font-display text-xl font-extrabold text-ink">
                    {title} <span className="text-base font-semibold text-ink-soft">({rows.length})</span>
                </h3>
                <button
                    type="button"
                    onClick={exportCsv}
                    disabled={rows.length === 0}
                    data-testid={`${testid}-export`}
                    className="inline-flex h-12 items-center gap-2 rounded-full border-2 border-teal-700 px-5 text-base font-semibold text-teal-700 transition-colors duration-200 hover:bg-teal-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <Download className="h-4 w-4" aria-hidden="true" />
                    Export CSV
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-base" data-testid={`${testid}-table`}>
                    <thead>
                        <tr className="border-b border-clay bg-sand/60">
                            {columns.map((c) => (
                                <th key={c.label} scope="col" className="whitespace-nowrap px-4 py-3 text-base font-bold text-ink">
                                    {c.label}
                                </th>
                            ))}
                            {(onDelete || onResend) && (
                                <th key="actions" scope="col" className="w-14 px-4 py-3">
                                    <span className="sr-only">Actions</span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + (onDelete || onResend ? 1 : 0)} className="px-4 py-10 text-center text-base text-ink-soft" data-testid={`${testid}-empty`}>
                                    Nothing yet — new entries appear here the moment someone submits.
                                </td>
                            </tr>
                        ) : (
                            rows.map((r, i) => (
                                <tr key={i} className="border-b border-clay/60 last:border-b-0 hover:bg-sand/40" data-testid={`${testid}-row-${i + 1}`}>
                                    {columns.map((c) => (
                                        <td key={c.label} className="px-4 py-3 align-top">
                                            {c.render ? c.render(r) : c.value(r)}
                                        </td>
                                    ))}
                                    {(onDelete || onResend) && (
                                        <td className="px-4 py-3 align-top">
                                            <div className="flex items-center gap-2">
                                                {onResend && r.owner_notified === false && (
                                                    <button
                                                        type="button"
                                                        data-testid={`${testid}-resend-${i + 1}`}
                                                        aria-label={`Resend notification for entry ${i + 1}`}
                                                        title="Send the notification email again"
                                                        disabled={busyId === r.id}
                                                        onClick={async () => {
                                                            setBusyId(r.id);
                                                            setFailedId(null);
                                                            try {
                                                                await onResend(r.id);
                                                            } catch {
                                                                setFailedId(r.id);
                                                            } finally {
                                                                setBusyId(null);
                                                            }
                                                        }}
                                                        className="inline-flex h-11 items-center gap-1.5 rounded-full border-2 border-teal-700 px-3.5 text-sm font-semibold text-teal-700 transition-colors duration-200 hover:bg-teal-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                                                    >
                                                        <RotateCw className={`h-4 w-4 ${busyId === r.id ? "animate-spin" : ""}`} aria-hidden="true" />
                                                        {busyId === r.id ? "Sending…" : "Resend"}
                                                    </button>
                                                )}
                                                {failedId === r.id && (
                                                    <span className="text-sm font-semibold text-[#b42318]" data-testid={`${testid}-resend-failed-${i + 1}`}>
                                                        Sending failed — try again
                                                    </span>
                                                )}
                                                {onDelete && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <button
                                                        type="button"
                                                        data-testid={`${testid}-delete-${i + 1}`}
                                                        aria-label={`Delete entry ${i + 1}`}
                                                        title="Delete this entry"
                                                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-clay text-ink-soft transition-colors duration-200 hover:border-[#b42318] hover:text-[#b42318]"
                                                    >
                                                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent className="max-w-md rounded-3xl border-clay bg-white" data-testid={`${testid}-delete-dialog-${i + 1}`}>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle className="font-display text-xl font-extrabold text-ink">
                                                            Delete this entry?
                                                        </AlertDialogTitle>
                                                        <AlertDialogDescription className="text-lg leading-relaxed text-ink-soft">
                                                            This permanently removes the record from the list. This
                                                            can&rsquo;t be undone.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel
                                                            data-testid={`${testid}-delete-cancel-${i + 1}`}
                                                            className="inline-flex h-12 items-center justify-center rounded-full border-2 border-ink px-5 text-base font-semibold text-ink hover:bg-ink hover:text-cream"
                                                        >
                                                            Cancel
                                                        </AlertDialogCancel>
                                                        <AlertDialogAction
                                                            data-testid={`${testid}-delete-confirm-${i + 1}`}
                                                            className="inline-flex h-12 items-center justify-center rounded-full bg-[#b42318] px-5 text-base font-semibold text-white hover:bg-[#8f1c13]"
                                                            onClick={() => onDelete(r.id)}
                                                        >
                                                            Delete
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}